/**
 * Chat Agent Kit
 *
 * Helper functions for integrating with the Chat Agent from anywhere in the app.
 * Components don't need to know about EventBus or event names.
 *
 * Usage:
 *   import { openChatAgent } from '@/lib/chatAgentKit';
 *
 *   openChatAgent({
 *     context: { pageName: 'Lesson Editor', pageData: this.lessonConfig },
 *     search: 'Help me improve this lesson',
 *   });
 */

import EventBus from '@/factories/eventBus';

const EVENTS = {
  OPEN: 'ChatAgent.open',
  CLOSE: 'ChatAgent.close',
  RESET: 'ChatAgent.reset',
  UPDATE_CONTEXT: 'ChatAgent.updateContext',
  SIDEBAR_STATE: 'ChatAgent.sidebarState',
};

/**
 * Open the chat agent with optional context and initial message.
 *
 * @param {Object} options
 * @param {Object} [options.context] - Backchannel context for the agent (not shown to user)
 * @param {string} [options.context.pageName] - Name of the current page/feature
 * @param {Object} [options.context.pageData] - Configuration or data from the current page
 * @param {string[]} [options.context.hints] - Helpful hints for the agent
 * @param {Object} [options.context.navigation] - Optional; merged at send time with `buildAppNavTree()` + current route
 * @param {string} [options.search] - Initial message to send immediately
 * @param {string} [options.prefill] - Pre-fill the input without sending (only when the instance has no messages)
 * @param {string} [options.instanceName] - Specific conversation instance to open
 * @param {boolean} [options.sidebar] - Open as in-flow sidebar instead of fixed overlay
 * @param {string} [options.displayName] - Display name for the conversation (skips first-message naming)
 */
export function openChatAgent(options = {}) {
  EventBus.$emit(EVENTS.OPEN, options);
}

/**
 * Close the chat agent.
 */
export function closeChatAgent() {
  EventBus.$emit(EVENTS.CLOSE);
}

/**
 * Reset the chat agent instance state.
 * Clears the current instance name and context so the next open() starts fresh.
 */
export function resetChatAgent() {
  EventBus.$emit(EVENTS.RESET);
}

/**
 * Replace the page-level context for the current chat session.
 * ChatAgent auto-stamps the active route so staleness can be detected later.
 * This does NOT affect navigationHistory, which is managed automatically.
 *
 * @param {Object} context - The new pageContext (replaces previous entirely)
 */
export function updateChatAgentContext(context) {
  EventBus.$emit(EVENTS.UPDATE_CONTEXT, context);
}

/**
 * Build a standardized context object for a specific page/feature.
 *
 * @param {Object} options
 * @param {string} options.pageName - Human-readable name of the page
 * @param {Object} [options.pageData] - Any configuration or data
 * @param {string[]} [options.hints] - Hints to help the agent
 * @returns {Object} Context object
 */
export function createContext({ pageName, pageData, hints = [], navigation }) {
  return {
    pageName,
    pageData,
    hints,
    ...(navigation ? { navigation } : {}),
    timestamp: Date.now(),
  };
}

/**
 * Listen for sidebar state changes.
 *
 * @param {Function} handler - Called with (active: boolean) when sidebar toggles
 * @returns {Function} Unsubscribe function
 */
export function onSidebarState(handler) {
  EventBus.$on(EVENTS.SIDEBAR_STATE, handler);
  return () => EventBus.$off(EVENTS.SIDEBAR_STATE, handler);
}

/**
 * Emit sidebar state change (called internally by ChatAgent).
 * @param {boolean} active
 */
export function emitSidebarState(active) {
  EventBus.$emit(EVENTS.SIDEBAR_STATE, active);
}

export { EVENTS as CHAT_AGENT_EVENTS };
